import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Avatar,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon
} from '@mui/icons-material';
import { useApiClient } from '../../config/axios';

interface User {
  username: string;
  displayName?: string;
  email?: string;
}

interface UserMenuProps {
  user: User;
  onLogout: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ user, onLogout }) => {
  const apiClient = useApiClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);

    try {
      await apiClient.post('/auth/logout');
      onLogout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
      handleClose();
    }
  };

  const displayName = user.displayName || user.username || 'User';
  const initials = displayName
    .split(' ')
    .map(name => name.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);

  return (
    <Box>
      <Button
        onClick={handleClick}
        startIcon={
          <Avatar sx={{ width: 28, height: 28, fontSize: 14 }}>
            {initials}
          </Avatar>
        }
        endIcon={<PersonIcon />}
        sx={{
          color: 'inherit',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {displayName}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 220,
            mt: 1.5
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled sx={{ pb: 2 }}>
          <ListItemIcon>
            <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
              {initials}
            </Avatar>
          </ListItemIcon>
          <ListItemText>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              @{user.username}
            </Typography>
            <Chip
              label="Logged In"
              color="success"
              size="small"
              sx={{ mt: 0.5, fontSize: '0.7rem' }}
            />
          </ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleLogout}
          disabled={isLoggingOut}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {isLoggingOut ? 'Signing out...' : 'Sign Out'}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserMenu;